import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface WebshopCustomer {
  id: string;
  webshop_id: number;
  auth_user_id: string | null;
  full_name: string;
  email: string;
  phone: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface WebshopAuthContextType {
  user: User | null;
  customer: WebshopCustomer | null;
  loading: boolean;
  refreshCustomer: () => Promise<void>;
  logout: () => Promise<void>;
}

const WebshopAuthContext = createContext<WebshopAuthContextType | undefined>(undefined);

// Export the context so it can be used in components
export { WebshopAuthContext };
export type { WebshopAuthContextType };

export function WebshopAuthProvider({ 
  children, 
  webshopId 
}: { 
  children: ReactNode; 
  webshopId: number;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [customer, setCustomer] = useState<WebshopCustomer | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCustomer = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('webshop_customers')
        .select('*')
        .eq('auth_user_id', userId)
        .eq('webshop_id', webshopId)
        .maybeSingle();

      if (error) throw error;
      setCustomer(data);
    } catch (error) {
      console.error('[WebshopAuth] Error fetching customer:', error);
      setCustomer(null);
    }
  };

  const refreshCustomer = async () => {
    if (user) {
      await fetchCustomer(user.id);
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setCustomer(null);
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        
        if (session?.user) {
          setTimeout(() => {
            fetchCustomer(session.user.id);
          }, 0);
        } else {
          setCustomer(null);
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchCustomer(session.user.id);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [webshopId]);

  return (
    <WebshopAuthContext.Provider value={{ user, customer, loading, refreshCustomer, logout }}>
      {children}
    </WebshopAuthContext.Provider>
  );
}

export function useWebshopAuth() {
  const context = useContext(WebshopAuthContext);
  if (context === undefined) {
    throw new Error('useWebshopAuth must be used within WebshopAuthProvider');
  }
  return context;
}
